import { STATUS_CODES } from 'utils/Constants'
import { getMiddleware, MiddlewareFunction } from './index'
import { siteDomain } from 'utils/CareguideDomains'
import { getAutologinRedirectUrl } from './utils'
import ViewerQuery from './query.viewer.middleware.gql'
import type { OutgoingHttpHeaders } from 'http'
import {
  AUTHENTICATED_ACCESS_PATHS,
  mustConfirmPassword,
  SUBSCRIBED_ACCESS_PATHS,
} from 'utils/auth'

export const handleAutoLoginRedirect: MiddlewareFunction = async (data, next) => {
  const { nextContext } = data
  const autologinToken = nextContext?.query?.altkn as string
  const emailDigest = nextContext?.query?.email_digest as string
  const { req, res } = nextContext

  if (!autologinToken && !emailDigest) {
    return next()
  }

  const gqlEndpoint = process.env.CGC_WEB_CGC_APP_URL || 'http://cgc-app:3000'

  const autologinParamString = !!autologinToken ? `autologin_token=${autologinToken}` : ''
  const digestParamString = !!emailDigest ? `email_digest=${emailDigest}` : ''

  const url = `${gqlEndpoint}/autologin?${autologinParamString}&${digestParamString}`
  const btoa = require('btoa')
  // eslint-disable-next-line no-undef
  const response = await fetch(url, {
    headers: {
      Authorization: `Basic ${btoa('careguidebeta:testdemo')}`,
      'x-cgc-domain': siteDomain(nextContext),
    },
  })
  const responseCookies = response.headers.get('set-cookie')
  const jsonResponse = await response.json()
  const autologinRedirectUrl = getAutologinRedirectUrl(req, autologinToken, emailDigest)
  let headers: OutgoingHttpHeaders = { Location: autologinRedirectUrl }

  if (jsonResponse.success === true) {
    const setCookie = require('set-cookie-parser')
    const splitCookieHeaders = setCookie.splitCookiesString(responseCookies)
    headers = { ...headers, 'Set-Cookie': splitCookieHeaders }
  }

  res!.writeHead(STATUS_CODES.CODE302, headers)
  res!.end()

  return {
    isServerSideRedirect: true,
  }
}

export const handleUnauthorizedAccess: MiddlewareFunction = async (
  { nextContext, apolloClient },
  next
) => {
  const { data } = await apolloClient.query({
    query: ViewerQuery,
  })

  const unauthorizedMiddlewares = [
    handleLoggedOutAccess,
    handleAutoLoggedInAccess,
    handleBasicAccess,
  ]

  const result = await getMiddleware(unauthorizedMiddlewares)({
    data,
    nextContext,
    apolloClient,
  })

  if (!result) {
    return next()
  } else {
    return result
  }
}

export const handleAutoLoggedInAccess: MiddlewareFunction = async ({ data, nextContext }, next) => {
  const { req, res } = nextContext

  if (mustConfirmPassword(req!.url!, data?.viewer?.authState)) {
    res!.writeHead(STATUS_CODES.CODE302, {
      Location: `/confirm-password?next=${encodeURIComponent(req?.url || '')}`,
    })
    res!.end()

    return {
      isServerSideRedirect: true,
    }
  } else {
    return next()
  }
}

export const handleLoggedOutAccess: MiddlewareFunction = async ({ data, nextContext }, next) => {
  const { req, res } = nextContext
  const isAuthenticatedPath = AUTHENTICATED_ACCESS_PATHS.find((p) => req!.url!.startsWith(p))

  if (!isAuthenticatedPath) {
    return next()
  }

  if (!data?.viewer?.user?.userId) {
    res!.writeHead(STATUS_CODES.CODE302, {
      Location: `/log-in`,
    })
    res!.end()

    return {
      isServerSideRedirect: true,
    }
  }
  return next()
}

export const handleBasicAccess: MiddlewareFunction = async ({ data, nextContext }, next) => {
  const { req, res } = nextContext
  const isUpgradedPath = SUBSCRIBED_ACCESS_PATHS.find((p) => req?.url?.startsWith(p))

  if (!isUpgradedPath) {
    return next()
  }

  if (!data?.viewer?.isSubscribed) {
    res!.writeHead(STATUS_CODES.CODE302, {
      Location: `/upgrade`,
    })
    res!.end()

    return {
      isServerSideRedirect: true,
    }
  }
  return next()
}
